import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@/lib/api';

// New: the "provision to change the app name in future" request. The backend
// now serves a public GET /config with the platform's display name, logo,
// accent color, and support contact — this fetches it once and makes it
// available anywhere in the app via useBranding(), instead of the old
// hardcoded app-name strings scattered through pages, emails, etc.
//
// Note this only affects in-app text/branding, not the native app's store
// listing name/icon for the Expo mobile apps — those are still baked in at
// build time regardless of this config (flagged in the expansion plan).

const DEFAULTS = {
  app_name: 'PaceSlot',
  logo_url: null,
  primary_color: '#D7FF00',
  support_email: null,
  support_phone: null,
  // Venue discovery radius (km), set by the super_admin.
  nearby_radius_km: 10,
};

const BrandingCtx = createContext({ ...DEFAULTS, refresh: () => {} });
export const useBranding = () => useContext(BrandingCtx);

// Fix (found during a Phase-0 branding audit): primary_color was fetched
// into context but nothing ever read it — the UI's accent color ("volt")
// was still a hardcoded hex baked into tailwind.config.js at build time.
// Tailwind's utility classes (bg-volt, text-volt/40, border-volt, …) are
// generated at build time, so they can't read a runtime value directly.
// The fix: tailwind.config.js now defines "volt" in terms of a CSS custom
// property (see that file), and this provider sets that property on
// <html> whenever primary_color changes — every existing bg-volt/text-volt/
// border-volt/opacity-modifier usage across the app picks it up for free,
// no per-component changes needed. index.css defines a default value so
// nothing breaks before this effect runs.
// Exported for the rare component (e.g. AdminDashboard's chart colors) that
// needs the configured accent as an rgba() string directly, rather than via
// the CSS variable this module sets on <html>.
export function hexToRgbTriplet(hex) {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

// A darker variant for the ":hover" shade, same technique Tailwind's own
// DEFAULT/hover pair used before this change (mirrors the old "#B3E600" ≈
// 90% of "#CCFF00").
function darken(rgbTriplet, factor = 0.85) {
  if (!rgbTriplet) return null;
  return rgbTriplet
    .split(' ')
    .map((n) => Math.round(Number(n) * factor))
    .join(' ');
}

function applyAccentColor(hex) {
  const rgb = hexToRgbTriplet(hex) || hexToRgbTriplet(DEFAULTS.primary_color);
  if (!rgb) return;
  const root = document.documentElement.style;
  root.setProperty('--color-volt', rgb);
  root.setProperty('--color-volt-hover', darken(rgb));
}

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULTS);

  const load = () => {
    let cancelled = false;
    api.get('/config')
      .then((r) => {
        if (!cancelled && r.data) {
          setBranding({ ...DEFAULTS, ...r.data });
          applyAccentColor(r.data.primary_color);
          if (r.data.app_name) document.title = `${r.data.app_name} — Book Courts Near You`;
        }
      })
      .catch(() => {
        // Config endpoint unreachable/not deployed yet — silently keep defaults
        // so the app still renders exactly as it does today.
      });
    return () => { cancelled = true; };
  };

  useEffect(() => load(), []);

  // Exposed so AdminSettings.jsx can make a saved change take effect across
  // the whole app immediately, without waiting for the next full reload.
  const value = { ...branding, refresh: load };

  return <BrandingCtx.Provider value={value}>{children}</BrandingCtx.Provider>;
};
